import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { PageLoader } from 'components/common/Loader'
import {
  cleanCallbackUrl,
  extractCallbackParams,
  storeSsoSession,
  takeReturnTo,
  validateSsoToken,
} from 'auth/ssoClient'

/**
 * SSO landing route — the main Kit19 app redirects here as
 * /callback?token=<JWT>&deviceId=<id>&returnUrl=<path>, exactly like the
 * Kit19Sales module's /callback. The JWT is validated server-side; on success
 * the user's details become the session every API call runs as.
 */
export default function SSOCallback() {
  const location = useLocation()
  const navigate = useNavigate()
  const [error, setError] = useState<string>('')
  const ran = useRef(false)

  useEffect(() => {
    if (ran.current) return
    ran.current = true

    const params = extractCallbackParams(location.search, location.hash)

    const fail = (message: string) => {
      setError(message)
      window.setTimeout(() => navigate('/login', { replace: true, state: { error: message } }), 3000)
    }

    if (params.error) {
      fail(params.errorDescription || params.error)
      return
    }

    const token = params.token
    if (!token || token.split('.').length !== 3) {
      fail('Invalid SSO token.')
      return
    }

    void (async () => {
      const result = await validateSsoToken(token)
      if (!result.valid || !result.userData) {
        fail(result.message || 'SSO validation failed.')
        return
      }

      storeSsoSession(token, result.userData, params.deviceId)
      cleanCallbackUrl()

      // URLSearchParams has already decoded returnUrl once — decoding again
      // would corrupt %2F/%2B inside a nested encrypted entityID.
      // Back to the view / edit the user asked for. The stash is exactly the path we
      // left from, so it wins over the echoed returnUrl: if the SSO page ever appends
      // that unencoded, the ?q= inside it is split off and we would land on a bare
      // /edit. Never to /login or /callback, which would loop; no target, the landing.
      const wanted = takeReturnTo() || params.returnUrl || '/'
      const safe = wanted.startsWith('/') && !/^\/(login|callback)(\/|\?|#|$)/.test(wanted) ? wanted : '/'
      navigate(safe, { replace: true })
    })()
  }, [location.hash, location.search, navigate])

  if (error) {
    return (
      <div className="app-shell">
        <div className="alert alert-error">
          <strong>Sign-in failed:</strong> {error}
          <div style={{ marginTop: 6, fontSize: 13 }}>Redirecting to login…</div>
        </div>
      </div>
    )
  }

  return <PageLoader />
}
